import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Encabezado } from '@/components/Estados';
import { useOpcionesLicencia } from '@/features/alumnos/queries';
import { fechaLarga, numero } from '@/lib/formato';

type ResultadoFechas = components['schemas']['ToolMembershipDatesResult'];
type ResultadoPres = components['schemas']['ToolClassPresentationsResult'];
type CuerpoFechas = components['schemas']['ToolMembershipDatesBody'];

function SelectorLicencia({
  valor,
  onCambio,
}: {
  valor: number | null;
  onCambio: (v: number | null) => void;
}) {
  const op = useOpcionesLicencia();
  return (
    <select
      className="input-vidrio"
      value={valor ?? ''}
      required
      onChange={(e) => onCambio(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">Elige una licencia</option>
      {op.data?.map((m) => (
        <option key={m.id} value={m.id}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

function FechasLicencias() {
  const editable = useCan('configuracion', 'editar');
  const { proteger, dialogo } = useStepUp();
  const [f, setF] = useState({
    membershipId: null as number | null,
    endsFrom: '',
    endsTo: '',
    onlyCurrent: false,
    kind: 'all' as 'all' | 'gifts' | 'paid',
    newEndsOn: '',
  });
  const [vista, setVista] = useState<ResultadoFechas | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const poner = (c: Partial<typeof f>) => {
    setF((v) => ({ ...v, ...c }));
    setVista(null);
  };
  const cuerpo = (dryRun: boolean): CuerpoFechas => ({
    membershipId: f.membershipId ?? 0,
    ...(f.endsFrom && { endsFrom: f.endsFrom }),
    ...(f.endsTo && { endsTo: f.endsTo }),
    onlyCurrent: f.onlyCurrent,
    kind: f.kind,
    newEndsOn: f.newEndsOn,
    dryRun,
  });
  const correr = async (aplicar: boolean) => {
    setOcupado(true);
    try {
      if (!aplicar) {
        setVista(
          await unwrap<ResultadoFechas>(
            api.POST('/api/v1/admin/tools/membership-dates/preview', { body: cuerpo(true) }),
          ),
        );
      } else {
        const r = await proteger(() =>
          unwrap<ResultadoFechas>(
            api.POST('/api/v1/admin/tools/membership-dates', { body: cuerpo(false) }),
          ),
        );
        toast.success(`${numero(r.toChange)} licencia(s) actualizada(s).`);
        setVista(null);
      }
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <section className="panel-vidrio space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Cambiar la fecha de fin de licencias</h2>
        <p className="text-sm text-texto-2">
          Por ejemplo: «todas las de regalo de ENARM 2026 que vencen el 20 de agosto pasan al 5 de
          octubre». Primero ves cuántas cambian.
        </p>
      </div>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void correr(false);
        }}
      >
        <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Campo etiqueta="Licencia *">
            <SelectorLicencia
              valor={f.membershipId}
              onCambio={(membershipId) => poner({ membershipId })}
            />
          </Campo>
          <Campo etiqueta="Terminan desde">
            <input
              type="date"
              className="input-vidrio"
              value={f.endsFrom}
              onChange={(e) => poner({ endsFrom: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Terminan hasta">
            <input
              type="date"
              className="input-vidrio"
              value={f.endsTo}
              onChange={(e) => poner({ endsTo: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Cuáles">
            <select
              className="input-vidrio"
              value={f.kind}
              onChange={(e) => poner({ kind: e.target.value as typeof f.kind })}
            >
              <option value="all">Todas</option>
              <option value="gifts">Solo de regalo</option>
              <option value="paid">Solo pagadas</option>
            </select>
          </Campo>
          <Campo etiqueta="Nueva fecha de fin *">
            <input
              type="date"
              className="input-vidrio"
              required
              value={f.newEndsOn}
              onChange={(e) => poner({ newEndsOn: e.target.value })}
            />
          </Campo>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={f.onlyCurrent}
              onChange={(e) => poner({ onlyCurrent: e.target.checked })}
            />
            Solo vigentes hoy
          </label>
        </fieldset>
        {editable && (
          <div className="flex justify-end gap-2">
            <button
              type="submit"
              className="btn-cs btn-vidrio"
              disabled={ocupado || !f.membershipId || !f.newEndsOn}
            >
              Ver cuántas cambian
            </button>
            {vista && vista.toChange > 0 && (
              <button
                type="button"
                className="btn-cs btn-peligro"
                disabled={ocupado}
                onClick={() => void correr(true)}
              >
                Cambiar {numero(vista.toChange)}
              </button>
            )}
          </div>
        )}
      </form>
      {vista && (
        <div className="space-y-2 rounded-lg border border-[var(--separador)] p-3 text-sm">
          <p>
            <strong>{numero(vista.matched)}</strong> licencia(s) en el filtro ·{' '}
            <strong>{numero(vista.toChange)}</strong> cambiarían
            {vista.alreadySame > 0 && ` · ${numero(vista.alreadySame)} ya tienen esa fecha`}
            {vista.skippedInvalid > 0 &&
              ` · ${numero(vista.skippedInvalid)} se omiten (empezarían después de la nueva fecha)`}
          </p>
          {vista.sample.length > 0 && (
            <table className="tabla-cs w-full text-xs">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th>Inicio</th>
                  <th>Fin actual</th>
                  <th>Regalo</th>
                </tr>
              </thead>
              <tbody>
                {vista.sample.map((s) => (
                  <tr key={`${s.userId}-${s.endsOn}`}>
                    <td>
                      {s.userName} <span className="text-texto-3">{s.userEmail}</span>
                    </td>
                    <td>{fechaLarga(s.startsOn)}</td>
                    <td>{fechaLarga(s.endsOn)}</td>
                    <td>{s.isGift ? 'Sí' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {dialogo}
    </section>
  );
}

function PresentacionesDeClases() {
  const editable = useCan('configuracion', 'editar');
  const { proteger, dialogo } = useStepUp();
  const [membershipId, setMembershipId] = useState<number | null>(null);
  const [vista, setVista] = useState<ResultadoPres | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const correr = async (dryRun: boolean) => {
    if (!membershipId) return;
    setOcupado(true);
    try {
      const r = await proteger(() =>
        unwrap<ResultadoPres>(
          api.POST('/api/v1/admin/tools/class-presentations', { body: { membershipId, dryRun } }),
        ),
      );
      if (dryRun) setVista(r);
      else {
        toast.success(`${numero(r.toAdd)} presentación(es) agregada(s) a la licencia.`);
        setVista(null);
      }
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setOcupado(false);
    }
  };
  return (
    <section className="panel-vidrio space-y-4">
      <div>
        <h2 className="text-lg font-extrabold">Presentaciones de las clases de una licencia</h2>
        <p className="text-sm text-texto-2">
          Agrega a la licencia las presentaciones que usan sus clases y todavía no tiene. (Para una
          sola clase está el botón en su pestaña Presentación.)
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Campo etiqueta="Licencia">
          <SelectorLicencia
            valor={membershipId}
            onCambio={(v) => {
              setMembershipId(v);
              setVista(null);
            }}
          />
        </Campo>
        {editable && (
          <>
            <button
              type="button"
              className="btn-cs btn-vidrio"
              disabled={!membershipId || ocupado}
              onClick={() => void correr(true)}
            >
              Ver cuáles faltan
            </button>
            {vista && vista.toAdd > 0 && (
              <button
                type="button"
                className="btn-cs btn-primario"
                disabled={ocupado}
                onClick={() => void correr(false)}
              >
                Agregar {numero(vista.toAdd)}
              </button>
            )}
          </>
        )}
      </div>
      {vista && (
        <div className="rounded-lg border border-[var(--separador)] p-3 text-sm">
          <p>
            {numero(vista.classes)} clase(s) en la licencia · <strong>{numero(vista.toAdd)}</strong>{' '}
            presentación(es) por agregar
          </p>
          {vista.sample.length > 0 && (
            <p className="mt-1 text-xs text-texto-2">{vista.sample.join(' · ')}</p>
          )}
        </div>
      )}
      {dialogo}
    </section>
  );
}

export function HerramientasPage() {
  return (
    <PermissionGate seccion="configuracion">
      <Encabezado
        titulo="Herramientas"
        descripcion="Cambios en bloque, siempre con vista previa. Quedan en la auditoría."
      />
      <div className="space-y-6">
        <FechasLicencias />
        <PresentacionesDeClases />
      </div>
    </PermissionGate>
  );
}
